// components/DeepCheck.jsx — 内部基本面对比工具（密码保护）
import React, { useState } from 'react';
import { theme } from '../theme';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API_BASE = 'https://insightstock-api-358482645476.us-west2.run.app';
const PASSWORD = 'stockaizerotoinfinity';

function fmt(val) {
  if (val === null || val === undefined) return '—';
  if (Math.abs(val) >= 1000) return (val / 1000).toFixed(1) + 'B';
  return val.toFixed(1) + 'M';
}

function fmtPct(val) {
  if (val === null || val === undefined) return '—';
  return val.toFixed(1) + '%';
}

function fmtNum(val) {
  if (val === null || val === undefined) return '—';
  if (Math.abs(val) >= 1000) return (val / 1000).toFixed(1) + 'B';
  if (Math.abs(val) >= 1) return val.toFixed(1) + 'M';
  return (val * 1000).toFixed(0) + 'K';
}

function MiniChart({ data, dataKey, title, height = 160 }) {
  if (!data || data.length === 0) return null;
  const reversed = [...data].reverse();
  return (
    <div style={{ marginBottom: '12px' }}>
      <p style={{ color: theme.textDark, fontSize: '11px', fontWeight: 600, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</p>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={reversed} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="period" tick={{ fill: theme.textDark, fontSize: 9 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: theme.textDark, fontSize: 9 }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '8px', fontSize: '12px' }}
            labelStyle={{ color: theme.text }}
            formatter={(value) => [value !== null ? value.toFixed(2) + 'M' : '—', title]}
          />
          <Bar dataKey={dataKey} radius={[3, 3, 0, 0]}>
            {reversed.map((entry, idx) => (
              <Cell key={idx} fill={entry[dataKey] >= 0 ? '#22c55e' : '#ef4444'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function StockCard({ data }) {
  if (!data) return null;
  const { ticker, company, stockPrice, financials, valuation, sharesOutstanding } = data;
  const c = company || {};
  const p = stockPrice || {};
  const v = valuation || {};
  const annual = financials?.annual || [];
  const quarterly = financials?.quarterly || [];

  return (
    <div style={{
      background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '16px',
      padding: '20px', minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h3 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '24px', fontWeight: 700, color: theme.teal, margin: '0 0 2px' }}>{ticker}</h3>
          <p style={{ color: theme.textDark, fontSize: '12px', margin: 0, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name || ''}</p>
          <p style={{ color: theme.textDark, fontSize: '11px', margin: '2px 0 0' }}>{c.industry || ''}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '22px', fontWeight: 700, color: theme.text, margin: '0 0 2px' }}>${p.latestClose || '—'}</p>
          <p style={{ fontSize: '13px', fontWeight: 600, color: p.dailyChange >= 0 ? '#22c55e' : '#ef4444', margin: 0 }}>
            {p.dailyChange !== undefined ? (p.dailyChange >= 0 ? '+' : '') + p.dailyChange + '%' : '—'}
          </p>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {[
          { label: 'Market Cap', value: c.market_cap ? fmtNum(c.market_cap / 1_000_000) : '—' },
          { label: 'Shares Out', value: sharesOutstanding ? fmtNum(sharesOutstanding) : '—' },
          { label: 'Employees', value: c.employees ? c.employees.toLocaleString() : '—' },
          { label: 'P/E', value: v.peRatio ? v.peRatio.toFixed(1) + 'x' : '—' },
          { label: 'P/S', value: v.psRatio ? v.psRatio.toFixed(1) + 'x' : '—' },
          { label: 'P/B', value: v.pbRatio ? v.pbRatio.toFixed(1) + 'x' : '—' },
          { label: 'EPS', value: v.eps !== undefined ? '$' + v.eps?.toFixed(2) : '—' },
          { label: 'Gross Margin', value: fmtPct(v.grossMargin) },
          { label: 'Op Margin', value: fmtPct(v.operatingMargin) },
          { label: 'Net Margin', value: fmtPct(v.netMargin) },
          { label: 'YTD Return', value: p.ytdReturn !== undefined && p.ytdReturn !== null ? (p.ytdReturn >= 0 ? '+' : '') + p.ytdReturn + '%' : '—' },
          { label: '1Y Return', value: p.return1Y !== undefined && p.return1Y !== null ? (p.return1Y >= 0 ? '+' : '') + p.return1Y + '%' : '—' },
        ].map((m, i) => (
          <div key={i} style={{ background: theme.bg, borderRadius: '8px', padding: '8px 10px' }}>
            <p style={{ color: theme.textDark, fontSize: '10px', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{m.label}</p>
            <p style={{ color: m.value === '—' ? theme.textDark : theme.text, fontSize: '13px', fontWeight: 600, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Balance Sheet Snapshot */}
      {annual.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
          {[
            { label: 'Total Assets', value: fmt(annual[0].totalAssets) },
            { label: 'Total Liab.', value: fmt(annual[0].totalLiabilities) },
            { label: 'Equity', value: fmt(annual[0].equity) },
            { label: 'Cash', value: fmt(annual[0].cash) },
            { label: 'Debt', value: fmt(annual[0].totalDebt) },
            { label: 'Op Cash Flow', value: fmt(annual[0].operatingCashFlow) },
          ].map((m, i) => (
            <div key={i} style={{ background: theme.bg, borderRadius: '8px', padding: '8px 10px' }}>
              <p style={{ color: theme.textDark, fontSize: '10px', margin: '0 0 2px', textTransform: 'uppercase' }}>{m.label}</p>
              <p style={{ color: theme.text, fontSize: '13px', fontWeight: 600, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <MiniChart data={quarterly} dataKey="revenue" title="Revenue (Quarterly $M)" />
      <MiniChart data={quarterly} dataKey="netIncome" title="Net Income (Quarterly $M)" />
      <MiniChart data={quarterly} dataKey="operatingIncome" title="Operating Income (Quarterly $M)" />
      <MiniChart data={annual} dataKey="revenue" title="Revenue (Annual $M)" height={130} />
      <MiniChart data={annual} dataKey="netIncome" title="Net Income (Annual $M)" height={130} />
    </div>
  );
}

export function DeepCheck({ onBack }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [tickerInput, setTickerInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [columns, setColumns] = useState(2);

  const handlePassword = (e) => {
    e.preventDefault();
    if (passwordInput === PASSWORD) {
      setAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Wrong password');
    }
  };

  const handleAnalyze = async () => {
    const tickers = tickerInput.toUpperCase().split(/[\s,;]+/).filter(t => t.length > 0 && t.length <= 6);
    if (tickers.length === 0) return;

    setLoading(true);
    setResults([]);
    const allResults = [];

    for (let i = 0; i < tickers.length; i++) {
      const t = tickers[i];
      setProgress(`Fetching ${t}... (${i + 1}/${tickers.length})`);
      try {
        const res = await fetch(API_BASE + '/api/stock/' + t + '/deep');
        const data = await res.json();
        allResults.push(data);
        setResults([...allResults]);
      } catch (err) {
        console.error(`Failed to fetch ${t}:`, err);
        allResults.push({ ticker: t, company: null, stockPrice: null, financials: {}, valuation: {} });
        setResults([...allResults]);
      }
    }

    setLoading(false);
    setProgress('');
  };

  // Password screen
  if (!authenticated) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ background: theme.bgCard, borderRadius: '16px', padding: '48px', maxWidth: '400px', width: '100%', border: '1px solid ' + theme.border, textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '28px', color: theme.text, margin: '0 0 8px' }}>Deep Check</h2>
          <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 28px' }}>Internal tool — password required</p>
          <form onSubmit={handlePassword}>
            <input
              type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)}
              placeholder="Enter password" autoFocus
              style={{ width: '100%', background: theme.bg, border: '1px solid ' + theme.border, borderRadius: '10px', padding: '14px 18px', color: theme.text, fontSize: '16px', marginBottom: '12px', outline: 'none', boxSizing: 'border-box' }}
            />
            {passwordError && <p style={{ color: theme.red, fontSize: '14px', margin: '0 0 12px' }}>{passwordError}</p>}
            <button type="submit" style={{ width: '100%', background: theme.teal, border: 'none', borderRadius: '10px', padding: '14px', color: '#0f172a', fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}>Enter</button>
          </form>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: theme.textDark, fontSize: '14px', cursor: 'pointer', marginTop: '16px' }}>← Back to Home</button>
        </div>
      </div>
    );
  }

  // Main tool
  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'DM Sans', sans-serif", color: theme.text }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={onBack} style={{ background: 'transparent', border: '1px solid ' + theme.border, borderRadius: '8px', padding: '10px 20px', color: theme.textMuted, fontSize: '14px', cursor: 'pointer' }}>← Back</button>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: 400, margin: 0 }}>Deep Check</h1>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ color: theme.textDark, fontSize: '12px' }}>Columns:</span>
            {[2, 3, 4].map(n => (
              <button key={n} onClick={() => setColumns(n)} style={{
                background: columns === n ? theme.teal : 'transparent', border: '1px solid ' + (columns === n ? theme.teal : theme.border),
                borderRadius: '6px', padding: '6px 12px', color: columns === n ? '#0f172a' : theme.textDark, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}>{n}</button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
          <input
            type="text" value={tickerInput} onChange={e => setTickerInput(e.target.value.toUpperCase())}
            placeholder="Enter tickers separated by spaces or commas (e.g. RKLB DPRO LAES LAC)"
            onKeyDown={e => { if (e.key === 'Enter') handleAnalyze(); }}
            style={{ flex: 1, background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '10px', padding: '14px 18px', fontSize: '15px', color: theme.text, fontFamily: "'JetBrains Mono', monospace", outline: 'none' }}
          />
          <button onClick={handleAnalyze} disabled={loading} style={{
            background: theme.teal, border: 'none', borderRadius: '10px', padding: '14px 32px',
            color: '#0f172a', fontSize: '15px', fontWeight: 700, cursor: loading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
          }}>
            {loading ? progress || 'Loading...' : 'Analyze'}
          </button>
        </div>

        {/* Results Grid */}
        {results.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '16px' }}>
            {results.map((data, i) => <StockCard key={data.ticker || i} data={data} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeepCheck;
